const DIRECT_CHECKOUT_MOUNTINGS = new Set([
  'product-only',
  'self-adhesive-inside',
  'glue-kit',
]);
const NETHERLANDS_SHIPPING_CENTS = 695;
const FREE_SHIPPING_THRESHOLD_CENTS = 5500;

class DuneSignConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.currency = this.dataset.currency || 'EUR';
    this.quoteUrl = this.dataset.quoteUrl || '/pages/offerte-aanvragen';
    this.pricing = window.DuneSignSoftLaunchPricing;
    this.products = {
      house: this.readProduct('house'),
      wallText: this.readProduct('wall-text'),
      icon: this.readProduct('icon'),
    };
    this.numberInput = this.querySelector('[data-ds-number]');
    this.wallTextInput = this.querySelector('[data-ds-wall-text]');
    this.substrateInput = this.querySelector('[data-ds-substrate]');
    this.substrateField = this.querySelector('[data-ds-substrate-field]');
    this.substrateError = this.querySelector('[data-ds-substrate-error]');
    this.substrateValid = this.querySelector('[data-ds-substrate-valid]');
    this.substrateValidationRequested = false;
    this.designValidationRequested = false;
    this.designError = this.querySelector('[data-ds-design-error]');
    this.colorsExpanded = false;
    this.submitButton = this.querySelector('[data-ds-submit]');
    this.checkoutAction = this.querySelector('[data-ds-checkout-action]');
    this.proposalHelper = this.querySelector('[data-ds-proposal-helper]');
    this.status = this.querySelector('[data-ds-status]');
    this.measureCanvas = document.createElement('canvas');
    this.selectTypeFromUrl();
    this.addEventListener('change', () => this.update());
    this.addEventListener('input', (event) => {
      if (event.target === this.numberInput) this.sanitizeHouseInput();
      this.update();
    });
    this.submitButton?.addEventListener('click', () => this.handleSubmit());
    this.querySelector('[data-ds-color-toggle]')?.addEventListener('click', () => {
      this.colorsExpanded = !this.colorsExpanded;
      this.update();
    });
    this.querySelectorAll('.ds-configurator__icon-visual img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', () => this.update(), { once: true });
    });
    this.handleViewportChange = () => {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        if (this.current?.state) this.updatePreview(this.current.state);
      });
    };
    window.addEventListener('resize', this.handleViewportChange, { passive: true });
    this.update();
    this.handleViewportChange();
    document.fonts?.ready.then(() => {
      this.update();
      this.handleViewportChange();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleViewportChange);
    window.cancelAnimationFrame(this.resizeFrame);
  }

  readProduct(key) {
    const script = this.querySelector(`script[data-ds-product="${key}"]`);
    if (!script?.textContent.trim()) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  selectTypeFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const requested = (params.get('type') || '').trim().toLowerCase();
    const aliases = { house: 'house', icon: 'icon', wall: 'wall-text', wandtekst: 'wall-text', 'wall-text': 'wall-text' };
    const type = aliases[requested];
    this.directType = type || '';
    if (type) {
      const input = this.querySelector(`[name="ds-product-type"][value="${type}"]`);
      if (input) input.checked = true;
    }

    const requestedApplication = (params.get('toepassing') || params.get('application') || '').trim().toLowerCase();
    const applicationAliases = { binnen: 'Binnen', inside: 'Binnen', buiten: 'Buiten', outside: 'Buiten' };
    const application = applicationAliases[requestedApplication];
    const applicationInput = this.querySelector(`[name="ds-application"][value="${application}"]`);
    if (application && applicationInput) applicationInput.checked = true;
  }

  sanitizeHouseInput() {
    const source = (this.numberInput?.value || '').toUpperCase().replace(/[^0-9A-Z -]/g, '');
    let printed = 0;
    let result = '';
    for (const character of source) {
      if (character !== ' ') printed += 1;
      if (printed > 6) break;
      result += character;
    }
    if (this.numberInput && result !== this.numberInput.value) this.numberInput.value = result;
  }

  selectedInput(name) {
    return this.querySelector(`[name="${name}"]:checked`);
  }

  selectedValue(name, fallback = '') {
    return this.selectedInput(name)?.value || fallback;
  }

  fieldValue(selector, fallback = '') {
    return this.querySelector(selector)?.value || fallback;
  }

  normalize(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  productCopy(type) {
    const key = type === 'wall-text' ? 'wall' : type;
    const fallback = {
      house: {
        title: 'Huisnummer op maat',
        input: 'Jouw huisnummer',
        applicationQuestion: 'Waar komt je huisnummer?',
        designLabel: 'Huisnummer',
        sizeQuestion: 'Kies de hoogte',
        widthLabel: 'Werkelijke breedte',
        errorEmpty: 'Vul eerst je huisnummer in.',
        emptyDimension: 'Vul eerst je huisnummer in',
        emptyPricePrompt: 'Vul je huisnummer in om direct jouw prijs te zien.',
        substrateQuestion: 'Waarop wil je het huisnummer bevestigen? *',
        errorSubstrate: 'Vul in waarop je het huisnummer wilt bevestigen, zodat wij de montagemethode kunnen controleren.',
      },
      wall: {
        title: 'Wandtekst op maat',
        input: 'Jouw wandtekst',
        applicationQuestion: 'Waar komt je wandtekst?',
        designLabel: 'Wandtekst',
        sizeQuestion: 'Kies de letterhoogte',
        widthLabel: 'Werkelijke breedte',
        errorEmpty: 'Vul eerst je wandtekst in.',
        emptyDimension: 'Vul eerst je wandtekst in',
        emptyPricePrompt: 'Vul je tekst in om direct jouw prijs te zien.',
        substrateQuestion: 'Waarop wil je de wandtekst bevestigen? *',
        errorSubstrate: 'Vul in waarop je de wandtekst wilt bevestigen, zodat wij de montagemethode kunnen controleren.',
      },
      icon: {
        title: 'Pictogram op maat',
        input: 'Jouw pictogram',
        applicationQuestion: 'Waar komt je pictogram?',
        designLabel: 'Pictogram',
        sizeQuestion: 'Kies het formaat',
        widthLabel: 'Berekende afmeting',
        errorEmpty: 'Kies eerst je pictogram.',
        emptyDimension: 'Kies eerst je pictogram',
        emptyPricePrompt: '',
        substrateQuestion: 'Waarop wil je het pictogram bevestigen? *',
        errorSubstrate: 'Vul in waarop je het pictogram wilt bevestigen, zodat wij de montagemethode kunnen controleren.',
      },
    }[key];
    return {
      title: this.dataset[`${key}Title`] || fallback.title,
      input: this.dataset[`${key}Input`] || fallback.input,
      applicationQuestion: this.dataset[`${key}ApplicationQuestion`] || fallback.applicationQuestion,
      designLabel: this.dataset[`${key}DesignLabel`] || fallback.designLabel,
      sizeQuestion: this.dataset[`${key}SizeQuestion`] || fallback.sizeQuestion,
      widthLabel: this.dataset[`${key}WidthLabel`] || fallback.widthLabel,
      errorEmpty: this.dataset[`${key}ErrorEmpty`] || fallback.errorEmpty,
      emptyDimension: this.dataset[`${key}EmptyDimension`] || fallback.emptyDimension,
      emptyPricePrompt: this.dataset[`${key}EmptyPricePrompt`] || fallback.emptyPricePrompt,
      substrateQuestion: this.dataset[`${key}SubstrateQuestion`] || fallback.substrateQuestion,
      errorSubstrate: this.dataset[`${key}ErrorSubstrate`] || fallback.errorSubstrate,
    };
  }

  productTitle(type) {
    return this.productCopy(type).title;
  }

  productionText(type) {
    if (type === 'house') return (this.numberInput?.value || '').trim().toUpperCase();
    if (type === 'wall-text') return (this.wallTextInput?.value || '').trim();
    return '';
  }

  houseSuffixState(text) {
    const characters = [...String(text || '')];
    let suffixIndex = characters.length - 1;
    while (suffixIndex >= 0 && characters[suffixIndex] === ' ') suffixIndex -= 1;
    const character = characters[suffixIndex] || '';
    const mainText = characters.slice(0, suffixIndex).join('');
    const compactMain = mainText.replace(/\s+/g, '');
    const detected = /^[A-Z]$/.test(character)
      && /\d/.test(compactMain)
      && !/[A-Z]/.test(compactMain);
    return {
      detected,
      character: detected ? character : '',
      mainText: detected ? mainText : String(text || ''),
    };
  }

  houseThickness(heightCm) {
    return { 10: 5, 20: 10, 30: 15 }[heightCm] || null;
  }

  measureHouseText(text, font, heightCm, suffix, suffixMode) {
    const fullWidth = this.measureText(text, font, heightCm);
    if (!fullWidth || !suffix.detected || suffixMode !== 'smaller') return fullWidth;
    const fullSuffixWidth = this.measureText(suffix.character, font, heightCm);
    if (!fullSuffixWidth) return fullWidth;
    const scaledSuffixWidth = this.measureText(suffix.character, font, heightCm * (2 / 3));
    if (!scaledSuffixWidth) return fullWidth;
    return Math.max(0, fullWidth - fullSuffixWidth + scaledSuffixWidth);
  }

  fontState() {
    const input = this.selectedInput('ds-style');
    return {
      key: input?.value || 'font-1',
      friendlyName: input?.dataset.title || 'Modern strak',
      technicalName: input?.dataset.technical || 'Assistant Regular 400',
      weight: Number(input?.dataset.weight || 400),
      referenceHeight: Number(input?.dataset.referenceHeight || 720),
      letterSpacing: Number(input?.dataset.letterSpacing || 0),
    };
  }

  measureText(text, font, heightCm) {
    if (!text) return null;
    const context = this.measureCanvas.getContext('2d');
    if (!context || !Number.isFinite(font.referenceHeight) || font.referenceHeight <= 0) return null;
    const family = getComputedStyle(this).getPropertyValue(`--ds-${font.key}`).trim();
    context.font = `${font.weight} 1000px ${family || 'sans-serif'}`;
    context.fontKerning = 'normal';
    const measured = context.measureText(text).width;
    const characters = [...text].length;
    const spacing = font.letterSpacing * 1000 * Math.max(0, characters - 1);
    const width = ((measured + spacing) * heightCm) / font.referenceHeight;
    return Number.isFinite(width) && width > 0 ? width : null;
  }

  iconRatio() {
    const selected = this.selectedInput('ds-icon');
    if (!selected) return null;
    const visual = selected.nextElementSibling?.querySelector('.ds-configurator__icon-visual');
    const svg = visual?.querySelector('svg');
    const viewBox = svg?.viewBox?.baseVal;
    if (viewBox?.width > 0 && viewBox?.height > 0) return viewBox.width / viewBox.height;
    const image = visual?.querySelector('img');
    if (image?.naturalWidth > 0 && image?.naturalHeight > 0) return image.naturalWidth / image.naturalHeight;
    return null;
  }

  iconDimensions(heightCm) {
    const ratio = this.iconRatio();
    if (!ratio) return null;
    if (ratio >= 1) return { widthCm: heightCm, heightCm: heightCm / ratio, ratio };
    return { widthCm: heightCm * ratio, heightCm, ratio };
  }

  iconVariant(state) {
    const product = this.products.icon;
    const searchedFormat = `${state.heightCm} cm`;
    const searchedMaterial = state.material === 'PETG' ? 'PETG buiten' : 'PLA binnen';
    if (!product?.variants?.length) {
      this.logIconVariantFailure('runtime-product-json-ontbreekt', searchedFormat, searchedMaterial, []);
      return null;
    }
    const heightTarget = this.normalize(`${state.heightCm} cm`);
    const materialTargets = new Set(
      state.material === 'PETG' ? ['petg', 'petgbuiten'] : ['pla', 'plabinnen'],
    );
    const variantsWithOptions = product.variants.map((variant) => ({
      variant,
      options: this.iconVariantOptions(variant),
    }));
    const matches = variantsWithOptions.filter(({ options }) => (
      options.includes(heightTarget) && options.some((option) => materialTargets.has(option))
    ));
    if (matches.length !== 1) {
      this.logIconVariantFailure(
        matches.length ? 'meerdere-varianten-gevonden' : 'variant-niet-gevonden',
        searchedFormat,
        searchedMaterial,
        variantsWithOptions,
      );
      return null;
    }
    this.lastIconVariantWarning = '';
    return matches[0].variant;
  }

  iconVariantOptions(variant) {
    const directOptions = Array.isArray(variant?.options) ? variant.options : [];
    const selectedOptions = Array.isArray(variant?.selectedOptions)
      ? variant.selectedOptions.map((option) => option?.value)
      : [];
    const titleOptions = typeof variant?.title === 'string' ? variant.title.split('/') : [];
    return [...directOptions, ...selectedOptions, ...titleOptions]
      .filter((option) => typeof option === 'string' && option.trim())
      .map((option) => this.normalize(option));
  }

  logIconVariantFailure(reason, format, material, variantsWithOptions) {
    const signature = `${reason}|${format}|${material}`;
    if (this.lastIconVariantWarning === signature) return;
    this.lastIconVariantWarning = signature;
    console.warn('[Dunesign configurator] Pictogramvariant kon niet veilig worden bepaald.', {
      reason,
      gezochtFormaat: format,
      gezochtMateriaal: material,
      beschikbareVarianten: variantsWithOptions.map(({ variant, options }) => ({
        id: variant?.id || '',
        options,
      })),
    });
  }

  wallTextVariant(state) {
    const product = this.products['wall-text'];
    if (state.type !== 'wall-text' || !product?.variants?.length) return null;
    const heightTarget = this.normalize(`${state.heightCm} cm`);
    const materialTarget = this.normalize(state.material);
    return product.variants.find((variant) => {
      const options = (variant.options || []).map((option) => this.normalize(option));
      const heightMatches = options.some((option) => option === heightTarget || option.includes(heightTarget));
      const materialMatches = options.some((option) => option === materialTarget || option.includes(materialTarget));
      return heightMatches && materialMatches;
    }) || null;
  }

  formatDimension(value, maximumFractionDigits = 1) {
    if (!Number.isFinite(value)) return '—';
    return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { maximumFractionDigits }).format(value);
  }

  money(cents) {
    if (!Number.isSafeInteger(cents)) return this.dataset.pricePending || 'Persoonlijk te bevestigen';
    return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
      style: 'currency',
      currency: this.currency,
    }).format(cents / 100);
  }

  setText(selector, value) {
    this.querySelectorAll(selector).forEach((element) => {
      element.textContent = value;
    });
  }

  setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.setAttribute('aria-live', isError ? 'assertive' : 'polite');
  }

  updateSubstrateValidation(substrate) {
    if (!this.substrateInput) return;
    const isValid = Boolean(substrate);
    const showError = this.substrateValidationRequested && !isValid;
    this.substrateField?.classList.toggle('ds-configurator__substrate-field--error', showError);
    this.substrateField?.classList.toggle('ds-configurator__substrate-field--valid', isValid);
    this.substrateInput.setAttribute('aria-invalid', String(showError));
    if (this.substrateError) this.substrateError.hidden = !showError;
    if (this.substrateValid) this.substrateValid.hidden = !isValid;
    if (isValid) this.substrateValidationRequested = false;
  }

  focusSubstrateField() {
    if (!this.substrateInput) return;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.substrateField?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    window.requestAnimationFrame(() => this.substrateInput?.focus({ preventScroll: true }));
  }

  updateDesignValidation(state) {
    const missingDesign = state.type === 'icon' ? !state.icon : !state.text;
    const showError = this.designValidationRequested && missingDesign;
    if (this.designError) this.designError.hidden = !showError;
    const input = state.type === 'house' ? this.numberInput : this.wallTextInput;
    input?.setAttribute('aria-invalid', String(showError));
    if (!missingDesign) this.designValidationRequested = false;
  }

  focusDesignField(state) {
    const input = state.type === 'house' ? this.numberInput : this.wallTextInput;
    const target = state.type === 'icon' ? this.querySelector('[data-ds-panel="icon"]') : input;
    if (!target) return;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    window.requestAnimationFrame(() => input?.focus({ preventScroll: true }));
  }

  getState() {
    const type = this.selectedValue('ds-product-type', 'house');
    const selectedIcon = this.selectedInput('ds-icon');
    const application = type === 'house' ? 'Buiten' : this.selectedValue('ds-application', 'Binnen');
    const material = application === 'Buiten' ? 'PETG' : 'PLA';
    const heightCm = Number(type === 'house'
      ? this.selectedValue('ds-house-size', '20')
      : this.selectedValue('ds-size', '12'));
    const font = this.fontState();
    const text = this.productionText(type);
    const suffix = type === 'house' ? this.houseSuffixState(text) : { detected: false, character: '', mainText: text };
    const suffixMode = suffix.detected ? this.selectedValue('ds-house-suffix-size', 'smaller') : '';
    const suffixScale = suffix.detected && suffixMode === 'smaller' ? 2 / 3 : suffix.detected ? 1 : null;
    const suffixHeightCm = suffix.detected ? heightCm * suffixScale : null;
    const actualWidthCm = type === 'icon'
      ? null
      : type === 'house'
        ? this.measureHouseText(text, font, heightCm, suffix, suffixMode)
        : this.measureText(text, font, heightCm);
    const iconDimensions = type === 'icon' ? this.iconDimensions(heightCm) : null;
    const surfaceInput = this.selectedInput('ds-surface');
    const mountInput = this.selectedInput('ds-mount');
    const colorInput = this.selectedInput('ds-color');
    return {
      type,
      productTitle: this.productTitle(type),
      text,
      icon: selectedIcon?.value || '',
      iconTitle: selectedIcon?.dataset.title || '',
      iconCode: selectedIcon?.dataset.iconCode || '',
      heightCm,
      thicknessMm: type === 'house' ? this.houseThickness(heightCm) : 10,
      application,
      material,
      color: colorInput?.dataset.friendlyName || colorInput?.value || '',
      colorTechnicalName: colorInput?.dataset.technicalName || colorInput?.value || '',
      colorHex: colorInput?.dataset.hex || '#171717',
      font,
      actualWidthCm,
      iconDimensions,
      suffixDetected: suffix.detected,
      suffixCharacter: suffix.character,
      suffixMainText: suffix.mainText,
      suffixMode,
      suffixScale,
      suffixHeightCm,
      surface: surfaceInput?.value || 'smooth',
      surfaceTitle: surfaceInput?.dataset.title || '',
      substrate: (this.substrateInput?.value || '').trim(),
      mountingChoice: mountInput?.value || 'product-only',
      mountingTitle: mountInput?.dataset.title || 'Alleen het product',
      mountingPriceCents: Number(mountInput?.dataset.priceCents || 0),
      mountingVariantId: mountInput?.dataset.variantId || '',
    };
  }

  updatePanels(state) {
    this.dataset.activeType = state.type;
    const copy = this.productCopy(state.type);
    this.querySelectorAll('[data-ds-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.dsPanel !== state.type;
    });
    this.querySelectorAll('[data-ds-font-field], [data-ds-font-step]').forEach((element) => {
      element.hidden = state.type === 'icon';
    });
    const applicationStep = this.querySelector('[data-ds-application-step]');
    if (applicationStep) applicationStep.hidden = state.type === 'house';
    this.querySelectorAll('[data-ds-size-group]').forEach((group) => {
      group.hidden = group.dataset.dsSizeGroup !== (state.type === 'house' ? 'house' : 'standard');
    });
    const houseMaterialNote = this.querySelector('[data-ds-house-material-note]');
    if (houseMaterialNote) houseMaterialNote.hidden = state.type !== 'house';
    const suffixField = this.querySelector('[data-ds-house-suffix-field]');
    if (suffixField) suffixField.hidden = state.type !== 'house' || !state.suffixDetected;
    this.setText('[data-ds-suffix-character]', state.suffixCharacter || '—');
    this.setText(
      '[data-ds-suffix-specification]',
      state.suffixDetected
        ? `${state.suffixCharacter}: ca. ${this.formatDimension(state.suffixHeightCm)} cm hoog × ${state.thicknessMm} mm dik`
        : '',
    );
    this.querySelectorAll('[data-ds-intro]').forEach((intro) => {
      intro.hidden = state.type === 'wall-text'
        ? intro.dataset.dsIntro !== 'wall-text'
        : intro.dataset.dsIntro !== 'default';
    });
    const productChoice = this.querySelector('[data-ds-product-choice]');
    if (productChoice) productChoice.hidden = Boolean(this.directType);
    this.querySelectorAll('[data-ds-size-legend]').forEach((sizeLegend) => {
      sizeLegend.textContent = state.type === 'icon' ? 'Grootste buitenmaat' : 'Letterhoogte';
    });
    const designHeading = this.querySelector('[data-ds-design-heading]');
    if (designHeading) designHeading.textContent = copy.input;
    this.setText('[data-ds-application-question]', copy.applicationQuestion);
    this.setText('[data-ds-size-question]', copy.sizeQuestion);
    this.setText('[data-ds-width-label]', copy.widthLabel);
    this.setText('[data-ds-substrate-question]', copy.substrateQuestion);
    if (this.designError) this.designError.textContent = copy.errorEmpty;
    if (this.substrateError) this.substrateError.textContent = copy.errorSubstrate;
    const thicknessNote = this.querySelector('[data-ds-thickness-note]');
    if (thicknessNote) {
      thicknessNote.textContent = state.type === 'house'
        ? `De dikte wordt automatisch ${state.thicknessMm} mm bij een hoogte van ${state.heightCm} cm.`
        : this.dataset.fixedThickness || 'Vaste productdikte: 10 mm.';
    }
    let visibleStep = 0;
    this.querySelectorAll('.ds-configurator__step').forEach((step) => {
      if (step.hidden) return;
      visibleStep += 1;
      const number = step.querySelector('.ds-configurator__step-number');
      if (number) number.textContent = String(visibleStep);
    });
  }

  updateColors(state) {
    const inputs = [...this.querySelectorAll('[name="ds-color"]')];
    const visibleCandidates = [];
    let extraCount = 0;
    inputs.forEach((input) => {
      const active = input.dataset.material === state.material;
      input.disabled = !active;
      const option = input.closest('[data-ds-color-option]');
      const isExtra = option?.dataset.dsColorExtra === 'true';
      if (active && isExtra) extraCount += 1;
      const visible = active && (!isExtra || this.colorsExpanded || input.checked);
      if (option) option.hidden = !visible;
      if (visible) visibleCandidates.push(input);
    });
    if (!visibleCandidates.some((input) => input.checked) && visibleCandidates[0]) visibleCandidates[0].checked = true;
    const toggle = this.querySelector('[data-ds-color-toggle]');
    if (toggle) {
      toggle.hidden = extraCount === 0;
      toggle.setAttribute('aria-expanded', String(this.colorsExpanded));
      toggle.textContent = this.colorsExpanded ? this.dataset.fewerColors : this.dataset.moreColors;
    }
    this.setText('[data-ds-color-material]', state.material === 'PETG' ? 'PETG · buiten' : 'PLA · binnen');
    this.setText(
      '[data-ds-material-note]',
      state.material === 'PETG' ? this.dataset.materialOutsideNote : this.dataset.materialInsideNote,
    );
  }

  updateMounts(state) {
    if (this.substrateInput) {
      const placeholder = state.surface === 'rough'
        ? this.substrateInput.dataset.placeholderRough
        : this.substrateInput.dataset.placeholderSmooth;
      if (placeholder) this.substrateInput.placeholder = placeholder;
    }
    this.querySelectorAll('[data-ds-mount-option]').forEach((option) => {
      const applications = option.dataset.applications?.split('|').filter(Boolean) || [];
      const surfaces = option.dataset.surfaces?.split('|').filter(Boolean) || [];
      const compatible = (!applications.length || applications.includes(state.application))
        && (!surfaces.length || surfaces.includes(state.surface));
      const inventoryAvailable = option.dataset.inventoryAvailable === 'true';
      const input = option.querySelector('input');
      const selectable = compatible && inventoryAvailable;
      option.hidden = !compatible;
      option.classList.toggle('ds-configurator__mount--unavailable', compatible && !inventoryAvailable);
      option.setAttribute('aria-disabled', String(!selectable));
      if (input) input.disabled = !selectable;
      const recommendedValue = state.surface === 'rough'
        ? 'glue-kit'
        : state.application === 'Buiten' ? 'self-adhesive-outside' : 'self-adhesive-inside';
      const recommended = compatible && inventoryAvailable && input?.value === recommendedValue;
      option.classList.toggle('ds-configurator__mount--recommended', recommended);
      const badge = option.querySelector('[data-ds-mount-recommendation]');
      if (badge) badge.hidden = !recommended;
    });
    const checked = this.selectedInput('ds-mount');
    if (checked?.disabled) {
      const productOnly = this.querySelector('[name="ds-mount"][value="product-only"]');
      if (productOnly) productOnly.checked = true;
    }
    const warning = this.querySelector('[data-ds-glue-warning]');
    if (warning) {
      const glueSelected = this.selectedValue('ds-mount') === 'glue-kit';
      const hidden = state.surface !== 'rough' && !glueSelected;
      warning.hidden = hidden;
      const details = warning.querySelector('details');
      if (hidden && details) details.open = false;
    }
  }

  calculatePrice(state) {
    if (!this.pricing) return { productPriceCents: null, paidWidthCm: null, rateCents: null, minimumApplied: false };
    const pricingMaterial = state.material === 'PETG' ? 'PETG buiten' : 'PLA binnen';
    if (state.type === 'wall-text' || state.type === 'house') {
      const printedCharacters = [...state.text].filter((character) => character !== ' ').length;
      if (!printedCharacters || !state.actualWidthCm) {
        return { productPriceCents: null, paidWidthCm: null, rateCents: null, minimumApplied: false, printedCharacters };
      }
      const hasSmallerSuffix = state.suffixDetected && state.suffixMode === 'smaller';
      const fullCharacters = printedCharacters - (hasSmallerSuffix ? 1 : 0);
      const calculation = this.pricing.calculateTextProductionPrice({
        actualWidthCm: state.actualWidthCm,
        heightCm: state.heightCm,
        thicknessMm: state.thicknessMm,
        material: pricingMaterial,
        fullCharacters,
        hasSmallerSuffix,
        applyLengthAdvantage: state.type === 'wall-text',
      });
      return {
        productPriceCents: calculation.totalCents,
        originalProductPriceCents: calculation.originalTotalCents ?? calculation.totalCents,
        lengthAdvantageCents: calculation.lengthAdvantageCents ?? 0,
        calculationBasis: calculation.calculationBasis,
        paidWidthCm: calculation.paidWidthCm,
        rateCents: calculation.rateCentsPerCm || calculation.rateCentsPerCharacter,
        rateUnit: calculation.calculationBasis === 'measured-width' ? 'cm' : 'character',
        rateCentsPerCm: calculation.rateCentsPerCm,
        rateCentsPerCharacter: calculation.rateCentsPerCharacter,
        suffixRateCents: state.suffixDetected
          ? hasSmallerSuffix ? calculation.smallerSuffixRateCents : calculation.rateCentsPerCharacter
          : null,
        suffixPriceCents: state.suffixDetected
          ? hasSmallerSuffix ? calculation.suffixCents : calculation.rateCentsPerCharacter
          : null,
        minimumApplied: calculation.minimumApplied,
        printedCharacters,
        fullCharacters,
        hasSmallerSuffix,
        rateVersion: calculation.rateVersion,
      };
    }
    const variant = this.iconVariant(state);
    const price = Number(variant?.price);
    return {
      productPriceCents: Number.isSafeInteger(price) ? price : null,
      paidWidthCm: null,
      rateCents: null,
      minimumApplied: false,
      iconVariantId: variant?.id || null,
    };
  }

  updatePreview(state) {
    this.querySelectorAll('[data-ds-preview="text"]').forEach((textPreview) => {
      textPreview.hidden = state.type === 'icon';
    });
    this.querySelectorAll('[data-ds-preview="icon"]').forEach((iconPreview) => {
      iconPreview.hidden = state.type !== 'icon';
    });
    this.querySelectorAll('[data-ds-preview-text]').forEach((previewText) => {
      const fallbackText = state.type === 'wall-text' ? 'Welkom' : '12A';
      const displayText = state.text || fallbackText;
      const suffix = state.type === 'house'
        ? (state.text ? {
          detected: state.suffixDetected,
          character: state.suffixCharacter,
          mainText: state.suffixMainText,
        } : this.houseSuffixState(fallbackText))
        : { detected: false, character: '', mainText: displayText };
      const previewMain = previewText.querySelector('[data-ds-preview-main]');
      const previewSuffix = previewText.querySelector('[data-ds-preview-suffix]');
      if (previewMain && previewSuffix) {
        previewMain.textContent = suffix.detected ? suffix.mainText : displayText;
        previewSuffix.textContent = suffix.detected ? suffix.character : '';
        previewSuffix.hidden = !suffix.detected;
        previewText.setAttribute('aria-label', displayText);
      } else {
        previewText.textContent = displayText;
      }
      previewText.parentElement.dataset.style = state.font.key;
      previewText.classList.toggle('ds-configurator__preview-number--wall', state.type === 'wall-text');
      previewText.classList.toggle('ds-configurator__preview-number--has-suffix', suffix.detected);
      previewText.classList.toggle(
        'ds-configurator__preview-number--smaller-suffix',
        suffix.detected && (!state.text || state.suffixMode === 'smaller'),
      );
      const baseSizeRem = state.type === 'wall-text'
        ? Math.min(10, Math.max(4.2, state.heightCm * 0.52))
        : Math.min(12, Math.max(6, state.heightCm * 0.6));
      previewText.style.fontSize = `${baseSizeRem}rem`;
      window.requestAnimationFrame(() => {
        const mockup = previewText.closest('.ds-configurator__mockup');
        const availableWidth = Math.max(0, (mockup?.clientWidth || 0) - 48);
        const measuredWidth = previewText.scrollWidth;
        if (availableWidth && measuredWidth > availableWidth) {
          const fittedSize = Math.max(1.4, baseSizeRem * (availableWidth / measuredWidth));
          previewText.style.fontSize = `${fittedSize}rem`;
        }
      });
    });
    const selectedVisual = this.selectedInput('ds-icon')?.nextElementSibling?.querySelector('.ds-configurator__icon-visual');
    this.querySelectorAll('[data-ds-preview-icon]').forEach((previewIcon) => {
      if (selectedVisual) previewIcon.innerHTML = selectedVisual.innerHTML;
      const iconSizeRem = Math.min(18, Math.max(9, state.heightCm * 0.8));
      previewIcon.style.width = `${iconSizeRem}rem`;
      previewIcon.style.height = `${iconSizeRem}rem`;
    });
    this.querySelectorAll('[data-ds-preview-object]').forEach((object) => {
      object.style.color = state.colorHex;
    });
  }

  dimensionsLabel(state, price) {
    if (state.type === 'icon') {
      if (!state.iconDimensions) return 'Bronverhouding controleren';
      return `${this.formatDimension(state.iconDimensions.widthCm)} × ${this.formatDimension(state.iconDimensions.heightCm)} cm · 10 mm`;
    }
    if (!state.actualWidthCm) return this.productCopy(state.type).emptyDimension;
    if (state.type === 'wall-text') {
      return `${this.formatDimension(state.actualWidthCm, 2)} cm werkelijk · ${price.paidWidthCm || '—'} cm betaald`;
    }
    return `${this.formatDimension(state.actualWidthCm, 2)} cm breed · ${state.heightCm} cm hoog · ${state.thicknessMm} mm dik`;
  }

  summaryDimensionsLabel(state) {
    if (state.type === 'icon') {
      if (!state.iconDimensions) return this.productCopy(state.type).emptyDimension;
      return `${this.formatDimension(state.iconDimensions.widthCm)} cm breed × ${this.formatDimension(state.iconDimensions.heightCm)} cm hoog`;
    }
    if (!state.actualWidthCm) return `— cm breed × ${this.formatDimension(state.heightCm)} cm hoog`;
    return `${this.formatDimension(state.actualWidthCm)} cm breed × ${this.formatDimension(state.heightCm)} cm hoog`;
  }

  priceDetail(state, price) {
    if (price.lengthAdvantageCents > 0) {
      return `${this.formatDimension(state.actualWidthCm, 2)} cm / ${price.paidWidthCm} cm. ${this.dataset.lengthAdvantageRule} ${this.dataset.lengthAdvantageNote.replace('__amount__', this.money(price.lengthAdvantageCents))}`;
    }
    if (price.calculationBasis === 'measured-width' && price.rateCentsPerCm) {
      const minimum = price.minimumApplied ? ' Minimumprijs €17,95 toegepast.' : '';
      return `${this.formatDimension(state.actualWidthCm, 2)} cm werkelijk, ${price.paidWidthCm} cm betaald × ${this.money(price.rateCentsPerCm)} per cm.${minimum}`;
    }
    if (price.calculationBasis === 'production-components' && price.rateCentsPerCharacter) {
      const minimum = price.minimumApplied ? ' Minimumprijs €17,95 toegepast.' : '';
      if (price.hasSmallerSuffix) {
        return `${price.fullCharacters} volledig(e) teken(s) × ${this.money(price.rateCentsPerCharacter)} + kleinere toevoegingsletter × ${this.money(price.suffixRateCents)}.${minimum}`;
      }
      return `${price.fullCharacters} volledig(e) teken(s) × ${this.money(price.rateCentsPerCharacter)}.${minimum}`;
    }
    if (state.type === 'icon' && price.productPriceCents !== null) return 'Prijs uit de beschikbare Shopify-variant voor formaat en materiaal.';
    return this.dataset.pricePending || 'Indicatieve prijs kan momenteel niet worden berekend.';
  }

  shippingCents(productAndMountingCents) {
    if (!Number.isFinite(productAndMountingCents)) return null;
    return productAndMountingCents < FREE_SHIPPING_THRESHOLD_CENTS ? NETHERLANDS_SHIPPING_CENTS : 0;
  }

  update() {
    const wallSize30 = this.querySelector('[data-ds-wall-size-30]');
    const wallSize30Input = wallSize30?.querySelector('input');
    const isWallText = this.selectedValue('ds-product-type', 'house') === 'wall-text';
    if (wallSize30 && wallSize30Input) {
      wallSize30.hidden = !isWallText;
      wallSize30Input.disabled = !isWallText;
      if (!isWallText && wallSize30Input.checked) {
        const standardSize = this.querySelector('[name="ds-size"][value="12"]');
        wallSize30Input.checked = false;
        if (standardSize) standardSize.checked = true;
      }
    }
    let state = this.getState();
    this.updateColors(state);
    state = this.getState();
    this.updateMounts(state);
    state = this.getState();
    const price = state.type === 'wall-text' && !state.actualWidthCm
      ? { productPriceCents: null, paidWidthCm: null, rateCents: null, minimumApplied: false }
      : this.calculatePrice(state);
    const totalPriceCents = price.productPriceCents === null
      ? null
      : price.productPriceCents + state.mountingPriceCents;
    const shippingCents = this.shippingCents(totalPriceCents);
    const checkoutTotalCents = totalPriceCents === null ? null : totalPriceCents + shippingCents;
    const missingCustomerInput = (state.type === 'house' || state.type === 'wall-text') && !state.text;
    const iconPriceUnavailable = state.type === 'icon' && price.productPriceCents === null;
    this.updatePanels(state);
    this.updatePreview(state);

    const design = state.type === 'icon' ? state.iconTitle : state.text;
    const actualWidthLabel = state.type === 'icon' && state.iconDimensions
      ? `${this.formatDimension(state.iconDimensions.widthCm)} × ${this.formatDimension(state.iconDimensions.heightCm)} cm`
      : state.actualWidthCm ? `${this.formatDimension(state.actualWidthCm, 2)} cm` : '—';
    const paidWidthLabel = state.type === 'wall-text' && price.paidWidthCm ? `${price.paidWidthCm} cm` : '—';
    const materialNote = state.material === 'PETG' ? this.dataset.materialOutsideNote : this.dataset.materialInsideNote;
    const copy = this.productCopy(state.type);
    const minimumCheckoutTotalCents = (this.pricing?.minimumCents || 0) + NETHERLANDS_SHIPPING_CENTS;
    const fromPrice = `${this.dataset.priceFrom || 'Vanaf'} ${this.money(minimumCheckoutTotalCents)} ${this.dataset.includingVatShort || 'incl. btw'}`;
    const priceContext = missingCustomerInput
      ? copy.emptyPricePrompt
      : `${state.heightCm} cm hoog · ${state.material} voor ${state.application.toLowerCase()}`;
    this.setText('[data-ds-summary-product]', state.productTitle);
    this.setText('[data-ds-summary-design]', design || '—');
    this.setText('[data-ds-summary-style]', state.font.friendlyName);
    this.setText('[data-ds-summary-dimensions]', this.summaryDimensionsLabel(state));
    this.setText('[data-ds-summary-thickness]', `${state.thicknessMm} mm`);
    this.setText('[data-ds-calculated-size]', this.dimensionsLabel(state, price));
    this.setText('[data-ds-actual-width]', actualWidthLabel);
    this.setText('[data-ds-paid-width]', paidWidthLabel);
    this.setText('[data-ds-summary-application]', state.application);
    this.setText('[data-ds-summary-material-note]', materialNote);
    this.setText('[data-ds-summary-color]', state.color || '—');
    this.setText('[data-ds-summary-surface]', state.substrate ? `${state.surfaceTitle} · ${state.substrate}` : state.surfaceTitle || '—');
    this.setText('[data-ds-summary-mount]', state.mountingTitle);
    const suffixSummaryRow = this.querySelector('[data-ds-summary-suffix-row]');
    if (suffixSummaryRow) suffixSummaryRow.hidden = state.type !== 'house' || !state.suffixDetected;
    this.setText(
      '[data-ds-summary-suffix]',
      state.suffixDetected
        ? `${state.suffixCharacter} · ${state.suffixMode === 'smaller' ? `kleiner, ca. ${this.formatDimension(state.suffixHeightCm)} cm hoog` : `zelfde formaat, ${state.heightCm} cm hoog`}`
        : '—',
    );
    this.setText(
      '[data-ds-product-price]',
      missingCustomerInput ? '—' : iconPriceUnavailable ? this.dataset.iconPriceUnavailableShort : this.money(price.productPriceCents),
    );
    const advantageNote = this.querySelector('[data-ds-length-advantage]');
    if (advantageNote) {
      advantageNote.hidden = missingCustomerInput || !(price.lengthAdvantageCents > 0);
      advantageNote.textContent = price.lengthAdvantageCents > 0
        ? this.dataset.lengthAdvantageNote.replace('__amount__', this.money(price.lengthAdvantageCents)) : '';
    }
    this.setText('[data-ds-mount-price]', this.money(state.mountingPriceCents));
    this.setText(
      '[data-ds-shipping-price]',
      missingCustomerInput || iconPriceUnavailable ? '—' : shippingCents === 0 ? this.dataset.shippingFreeLabel || 'Gratis' : this.money(shippingCents),
    );
    this.setText(
      '[data-ds-total]',
      missingCustomerInput ? fromPrice : iconPriceUnavailable ? this.dataset.iconPriceUnavailableShort : this.money(checkoutTotalCents),
    );
    this.setText(
      '[data-ds-price-detail]',
      iconPriceUnavailable ? this.dataset.iconPriceUnavailable : this.priceDetail(state, price),
    );
    this.setText('[data-ds-price-context]', priceContext);
    this.setText('[data-ds-summary-heading]', copy.input);
    this.setText('[data-ds-summary-design-label]', copy.designLabel);
    const styleRow = this.querySelector('[data-ds-summary-style-row]');
    if (styleRow) styleRow.hidden = state.type === 'icon';
    const paidWidthCard = this.querySelector('[data-ds-paid-width-card]');
    if (paidWidthCard) paidWidthCard.hidden = state.type !== 'wall-text';
    const priceCalculation = this.querySelector('[data-ds-price-calculation]');
    if (priceCalculation) {
      priceCalculation.hidden = missingCustomerInput;
      if (missingCustomerInput) priceCalculation.open = false;
    }

    const missingDesign = state.type === 'icon' ? !state.icon : !state.text;
    const printedCharacters = state.type === 'house' ? [...state.text].filter((character) => character !== ' ').length : 0;
    const invalidHouse = state.type === 'house' && printedCharacters > 6;
    const checkoutButton = this.querySelector('[data-ds-checkout]');
    const checkoutProduct = state.type === 'wall-text' || state.type === 'house';
    const checkoutEligible = checkoutProduct
      && !missingDesign
      && Boolean(state.substrate)
      && DIRECT_CHECKOUT_MOUNTINGS.has(state.mountingChoice);
    if (checkoutButton) {
      checkoutButton.disabled = !checkoutEligible;
      checkoutButton.setAttribute('aria-disabled', String(!checkoutEligible));
    }
    if (this.checkoutAction) this.checkoutAction.hidden = !checkoutEligible;
    if (this.submitButton) {
      this.submitButton.classList.toggle('ds-configurator__submit--secondary', checkoutEligible);
      this.submitButton.textContent = checkoutEligible
        ? this.dataset.submitAdviceLabel || 'Liever eerst persoonlijk advies? Vraag een persoonlijk voorstel →'
        : this.dataset.submitLabel || 'Persoonlijk voorstel aanvragen →';
    }
    if (this.proposalHelper) this.proposalHelper.hidden = !checkoutEligible;
    this.updateDesignValidation(state);
    this.updateSubstrateValidation(state.substrate);
    this.submitButton.disabled = false;

    if (missingDesign && this.designValidationRequested) this.setStatus(copy.errorEmpty, true);
    else if (missingDesign) this.setStatus('');
    else if (invalidHouse) this.setStatus(this.dataset.errorHouseLimit || 'Gebruik maximaal zes geprinte tekens.', true);
    else if (iconPriceUnavailable) this.setStatus(this.dataset.iconPriceUnavailable || 'De prijs voor deze combinatie kon niet worden geladen. Probeer de pagina opnieuw of neem contact met ons op.', true);
    else this.setStatus('');

    this.current = { state, price, totalPriceCents, shippingCents, checkoutTotalCents };
    this.handleViewportChange?.();
  }

  handleSubmit() {
    this.update();
    const current = this.current;
    if (!current) {
      this.setStatus(this.dataset.errorEmpty || 'Controleer de gemarkeerde gegevens.', true);
      return;
    }
    const missingDesign = current.state.type === 'icon' ? !current.state.icon : !current.state.text;
    const printedCharacters = current.state.type === 'house'
      ? [...current.state.text].filter((character) => character !== ' ').length
      : 0;
    const copy = this.productCopy(current.state.type);
    if (missingDesign || printedCharacters > 6) {
      this.designValidationRequested = true;
      this.updateDesignValidation(current.state);
      this.setStatus(
        printedCharacters > 6
          ? this.dataset.errorHouseLimit || 'Gebruik maximaal zes geprinte tekens.'
          : copy.errorEmpty,
        true,
      );
      this.focusDesignField(current.state);
      return;
    }
    if (!current.state.substrate) {
      this.substrateValidationRequested = true;
      this.updateSubstrateValidation(current.state.substrate);
      this.setStatus('');
      this.focusSubstrateField();
      return;
    }
    const { state, price, totalPriceCents } = current;
    const wallTextVariant = this.wallTextVariant(state);
    const configurationId = `DS-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    const payload = {
      schemaVersion: 'dunesign-soft-launch-review-v1',
      configurationId,
      rateVersion: price.rateVersion || this.pricing?.rateVersion || 'soft-launch-pricing-2026-08-25',
      createdAt: new Date().toISOString(),
      productKey: state.type,
      product: state.productTitle,
      enteredText: state.type === 'icon' ? '' : state.text,
      chosenIcon: state.type === 'icon' ? state.iconTitle : '',
      iconReference: state.type === 'icon' ? state.icon : '',
      fontFriendlyName: state.type === 'icon' ? '' : state.font.friendlyName,
      fontTechnicalName: state.type === 'icon' ? '' : state.font.technicalName,
      fontWeight: state.type === 'icon' ? '' : state.font.weight,
      heightCm: state.heightCm,
      thicknessMm: state.thicknessMm,
      suffixDetected: state.suffixDetected,
      suffixCharacter: state.suffixCharacter,
      suffixMode: state.suffixMode,
      suffixScale: state.suffixScale,
      detectedSuffix: state.suffixDetected ? state.suffixCharacter : '',
      suffixSizeMode: state.suffixDetected
        ? state.suffixMode === 'smaller' ? 'Kleiner – aanbevolen' : 'Zelfde formaat'
        : '',
      suffixHeightCm: state.suffixDetected ? Number(state.suffixHeightCm.toFixed(2)) : '',
      suffixWidthScale: state.suffixDetected ? (state.suffixMode === 'smaller' ? '2/3' : '1/1') : '',
      suffixThicknessMm: state.suffixDetected ? state.thicknessMm : '',
      actualWidthCm: state.actualWidthCm ? Number(state.actualWidthCm.toFixed(6)) : '',
      paidWidthCm: price.paidWidthCm || '',
      iconWidthCm: state.iconDimensions ? Number(state.iconDimensions.widthCm.toFixed(2)) : '',
      iconHeightCm: state.iconDimensions ? Number(state.iconDimensions.heightCm.toFixed(2)) : '',
      application: state.application,
      material: state.material,
      color: state.color,
      colorTechnicalName: state.colorTechnicalName,
      variantId: wallTextVariant?.id || '',
      surfaceCategory: state.surfaceTitle,
      substrate: state.substrate,
      mountingChoice: state.mountingTitle,
      originalProductPriceCents: price.originalProductPriceCents ?? price.productPriceCents,
      lengthAdvantageCents: price.lengthAdvantageCents ?? 0,
      productPriceCents: price.productPriceCents,
      productPrice: this.money(price.productPriceCents),
      mountingPriceCents: state.mountingPriceCents,
      mountingPrice: this.money(state.mountingPriceCents),
      mountingVariantId: state.mountingVariantId,
      totalPriceCents,
      totalPrice: this.money(totalPriceCents),
      rateCents: price.rateCents || '',
      rateUnit: price.rateUnit || '',
      rateCentsPerCm: price.rateCentsPerCm || '',
      rateCentsPerCharacter: price.rateCentsPerCharacter || '',
      calculationBasis: price.calculationBasis || '',
      suffixRateCents: price.suffixRateCents || '',
      suffixPriceCents: price.suffixPriceCents || '',
      fullPriceCharacters: price.fullCharacters ?? '',
      minimumApplied: price.minimumApplied ? 'Ja' : 'Nee',
      iconVariantId: price.iconVariantId || '',
      priceStatus: price.productPriceCents === null ? 'Persoonlijk te bepalen' : 'Indicatie volgens soft-launchregels',
    };
    try {
      sessionStorage.setItem('dunesign:soft-launch-configuration:v1', JSON.stringify(payload));
    } catch {
      this.setStatus('De aanvraag kon niet tijdelijk worden bewaard. Probeer het opnieuw.', true);
      return;
    }
    const target = new URL(this.quoteUrl, window.location.origin);
    target.searchParams.set('bron', 'soft-launch-configurator');
    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId && /^\d+$/.test(previewThemeId)) target.searchParams.set('preview_theme_id', previewThemeId);
    target.hash = 'DuneSignQuoteForm';
    this.submitButton.disabled = true;
    window.location.assign(`${target.pathname}${target.search}${target.hash}`);
  }
}

if (!customElements.get('dunesign-configurator')) {
  customElements.define('dunesign-configurator', DuneSignConfigurator);
}


